import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from "tss-react/mui";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import DialogActions from 'components/DialogActions';
import SecondaryDialogButton from 'components/SecondaryDialogButton';
import PrimaryDialogButton from 'components/PrimaryDialogButton';
import { Subtitle2, Body2 } from 'components/Typography';
import FileHandler from 'utilities/files';
import { bytesToMb } from 'utilities/formatters/fileSizeFormatter';
import styles from './styles';

class AddEvidenceDialog extends React.Component {

    constructor(props) {
        super(props);

        this.fileRef = React.createRef();
        this.fileHandler = new FileHandler();

        let suppliedPreview = null;
        if (props.selectedFileContents) {
            suppliedPreview = props.selectedFileContents;
        }

        this.state = {
            selectedFile: null,
            selectedContents: suppliedPreview,
            initialContents: suppliedPreview,
            errorMessages: [],
            error: false
        };

        this.defaultMessages = {
            fileSize: `Maximum supported file size ${bytesToMb(props.config.maxFileSizeInBytes)}`,
            fileTypes: `Supported file types JPG, or PNG format`
        };
    }

    onClose = () => {
        this.setState({
            selectedFile: null,
            selectedContents: null,
            initialContents: null
        });
        this.props.handleClose();
    };

    remove = () => {
        this.setState({
            selectedFile: null,
            selectedContents: null,
            initialContents: null
        });
        this.props.onFileSelectionChanged(null);
        this.props.handleApply(null);
    };

    apply = async () => {

        try {
            this.fileHandler.file = this.state.selectedFile;
            const contents = await this.fileHandler.readBinaryContents();
            this.setState({ selectedContents: contents, initialContents: contents });
            this.props.handleApply(contents);

        } catch (err) {
            this.props.handleFileError(err);
        }
    };

    get canApply() {
        return this.state.selectedFile !== null;
    }

    handleFileChange = (event) => {
        if (event.currentTarget.files && event.currentTarget.files.length === 1) {
            const file = event.currentTarget.files[0];
            const errorMessages = [];

            this.setState({
                selectedFile: file,
                errorMessages: errorMessages,
                error: false
            });

            if (file.size > this.props.config.maxFileSizeInBytes) {
                errorMessages.push('fileSize');
                this.setState({
                    selectedFile: null,
                    errorMessages: errorMessages,
                    error: true
                });
            }

            // file.type relies on a mapping between file extension and mime type so may be empty if the file
            // extension is unknown to the OS.  As images should definitely be understood here, an empty type is
            // treated as an invalid extension
            if (!file.type || file.type.length === 0 || !this.props.config.supportedEvidenceFileTypes.includes(file.type)) {
                errorMessages.push('fileTypes');
                this.setState({
                    selectedFile: null,
                    errorMessages: errorMessages,
                    error: true
                });
            }

            if (errorMessages.length === 0) {
                this.props.onFileSelectionChanged(file);

                const _this = this;

                this.fileHandler.file = file;
                this.fileHandler.readAsDataUrl()
                    .then(contents => {
                        _this.setState({ selectedContents: contents });
                    });
            }
        }
    };

    handleClick = () => {
        this.fileRef.current.click();
    };

    get fileMessage() {
        if (this.state.selectedFile) {
            return this.state.selectedFile.name;
        }

        return "No file selected";
    }

    get validationMessages() {
        const _this = this;
        const { classes } = this.props;
        var content;
        if (!this.state.error) {
            content =
                <Body2 className={this.state.error ? classes.error : ""} >
                    {this.defaultMessages['fileSize']}
                </Body2>;
        } else {
            content = this.state.errorMessages.map((e, index) =>
                <Body2 key={index} className={classes.error}>
                    {_this.defaultMessages[e]}
                </Body2>
            );
        }
        return (content);
    }

    existingImage() {
        const { selectedContents } = this.state;
        const image = 'data:image/jpeg;base64,' + selectedContents;
        return (
            <div>
                <Grid container justifyContent="center" alignContent="center" spacing={2}>
                    <img src={image} style={{ maxHeight: 200 }} />
                </Grid>
            </div>);
    }

    noInitialImage() {
        const { classes, config } = this.props;
        let { selectedContents } = this.state;
        const fileMessage = this.fileMessage;

        if (selectedContents && !selectedContents.startsWith('data:')) {
            selectedContents = 'data:image/jpeg;base64,' + selectedContents;
        }

        return (
            <div>
                <input
                    type="file"
                    title="Select file"
                    className={classes.offscreen}
                    ref={this.fileRef}
                    id="fileChooser"
                    name="fileChooser"
                    capture="environment"
                    accept={config.supportedEvidenceFileTypes}
                    onChange={this.handleFileChange} />

                <Grid container justifyContent="flex-start" alignContent="flex-start" spacing={2}>
                    <Grid item>
                        <PrimaryDialogButton onClick={this.handleClick}>Browse</PrimaryDialogButton>
                    </Grid>
                    <Grid item>
                        <Subtitle2>{fileMessage}</Subtitle2>
                    </Grid>
                </Grid>
                <Grid container justifyContent="flex-start" alignContent="flex-start" spacing={2}>
                    <Grid item>
                        {this.validationMessages}
                    </Grid>
                </Grid>
                {selectedContents &&
                    <Grid container justifyContent="center" alignContent="center" spacing={2}>
                        <img src={selectedContents} style={{ maxHeight: 200 }} />
                    </Grid>
                }
            </div>);
    }

    render() {
        const { classes } = this.props;
        const hasInitialContents = this.state.initialContents;//|| this.state.selectedFile;
        const title = hasInitialContents ? 'Selected evidence' : 'Select evidence';

        return (
            <Dialog
                open={this.props.open}
                onClose={this.props.handleClose}>
                <DialogTitle>
                    {title}
                </DialogTitle>
                <DialogContent className={classes.content}>
                    <form>
                        {hasInitialContents && this.existingImage()}
                        {!hasInitialContents && this.noInitialImage()}
                    </form>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    {!hasInitialContents &&
                        <Fragment>
                            <SecondaryDialogButton onClick={this.onClose}>Cancel</SecondaryDialogButton>
                            <PrimaryDialogButton disabled={!this.canApply} onClick={this.apply}>Add</PrimaryDialogButton>
                        </Fragment>
                    }
                    {hasInitialContents &&
                        <Fragment>
                            <SecondaryDialogButton onClick={this.remove}>Remove</SecondaryDialogButton>
                            <PrimaryDialogButton onClick={this.props.handleClose}>OK</PrimaryDialogButton>
                        </Fragment>
                    }
                </DialogActions>
            </Dialog>
        );
    }
}

AddEvidenceDialog.propTypes = {
    handleClose: PropTypes.func.isRequired,
    handleApply: PropTypes.func.isRequired,
    handleFileError: PropTypes.func.isRequired,
    onFileSelectionChanged: PropTypes.func.isRequired,
    selectedFileContents: PropTypes.string
};

export default withStyles(AddEvidenceDialog, styles);